import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { performanceService } from '../../services/performanceService';
import { useSnackbar } from '../../utils/useSnackbar';
import ReviewActionDialog from '../../utils/ReviewActionDialog';
import TableSkeleton from '../common/TableSkeleton';
import PageHeader from '../ui/PageHeader';
import {
  REVIEW_TYPE,
  REVIEW_TYPE_LABELS,
  REVIEW_TYPE_TABS,
  REVIEW_TYPE_HAS_MILESTONE,
  REVIEW_STATUS,
  REVIEW_STATUS_OPTIONS,
} from '../../constants/reviewType';
import '../../css/Leave.css';

const MONITORING_ROLES = ['ADMIN', 'SUPER_ADMIN', 'MANAGE_JOBS'];
// TL (MANAGE_JOBS) can only see these review types
const TL_ALLOWED_TABS = [
  REVIEW_TYPE.MONTHLY_PROGRESS_REVIEW,
  REVIEW_TYPE.QUARTERLY_PERFORMANCE_REVIEW,
  REVIEW_TYPE.PROBATION_PERIOD_END
];

const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
};

const statusChipProps = (status) => {
  switch (status) {
    case REVIEW_STATUS.COMPLETED: return { color: 'success', label: 'Completed' };
    case REVIEW_STATUS.SKIPPED: return { color: 'default', label: 'Skipped' };
    default: return { color: 'warning', label: 'Pending' };
  }
};

const EmployeeMonitoring = () => {
  const { data: userData } = useSelector((state) => state.userDetails);
  const userRoles = useMemo(() => userData?.roles ?? [], [userData]);
  const userId = userData?.id;
  const reviewerName = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim();
  const navigate = useNavigate();

  const canAccess = useMemo(
    () => userRoles.some((r) => MONITORING_ROLES.includes(r)),
    [userRoles]
  );

  const isTL = useMemo(
    () => userRoles.includes('MANAGE_JOBS') && !userRoles.includes('ADMIN') && !userRoles.includes('SUPER_ADMIN'),
    [userRoles]
  );

  // TLs only see the first two tabs; admins see all four
  const visibleTabs = useMemo(
    () => isTL ? REVIEW_TYPE_TABS.filter((t) => TL_ALLOWED_TABS.includes(t.value)) : REVIEW_TYPE_TABS,
    [isTL]
  );

  // ── Filters ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const reviewType = visibleTabs[activeTab]?.value ?? visibleTabs[0].value;
  const showMilestone = REVIEW_TYPE_HAS_MILESTONE.includes(reviewType);

  // ── Pagination / sort ─────────────────────────────────────────────────
  const [records, setRecords] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([{ field: 'createdAt', sort: 'desc' }]);
  const isSearchFirstRender = useRef(true);
  const isSortFirstRender = useRef(true);

  // ── Action dialog ─────────────────────────────────────────────────────
  const [pendingAction, setPendingAction] = useState(null); // { row, status }
  const [actionComment, setActionComment] = useState('');

  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // ── Debounce search ───────────────────────────────────────────────────
  useEffect(() => {
    if (isSearchFirstRender.current) { isSearchFirstRender.current = false; return; }
    const t = setTimeout(() => {
      const v = search.trim();
      if (v === '' || v.length > 2) {
        setDebouncedSearch(v);
        setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
      }
    }, 500);
    return () => clearTimeout(t);
  }, [search]);

  // ── Fetch ─────────────────────────────────────────────────────────────
  const fetchReviews = async () => {
    if (!userId || !canAccess) return;
    try {
      setLoading(true);
      const sortField = sortModel?.[0]?.field || 'createdAt';
      const sortDir = sortModel?.[0]?.sort || 'desc';
      const data = await performanceService.getReviewsByType(
        userId, userRoles,
        reviewType, status, debouncedSearch,
        paginationModel.page, paginationModel.pageSize,
        sortField, sortDir,
      );
      setRecords(data?.content ?? []);
      setRowCount(data?.totalElements ?? 0);
    } catch (err) {
      if (err?.response?.status === 404) {
        setRecords([]); setRowCount(0);
      } else {
        const msg = err?.response?.data?.message || err?.response?.data || 'Failed to load reviews.';
        showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
        setRecords([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, canAccess, reviewType, status, debouncedSearch, paginationModel, sortModel]);

  // Reset page when tab changes
  useEffect(() => {
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [activeTab, status]);

  const handleRefresh = () => {
    setSearch('');
    setDebouncedSearch('');
    setStatus('');
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    fetchReviews();
  };

  const handleSortModelChange = (newModel) => {
    if (isSortFirstRender.current) {
      isSortFirstRender.current = false;
      if (!newModel?.length) return;
    }
    const next = newModel?.[0];
    const current = sortModel?.[0];
    if (next?.field === current?.field && next?.sort === current?.sort) return;

    if (!newModel?.length && sortModel.length > 0) {
      const currentField = sortModel[0].field;
      const isColumnDefined = columns.some((c) => c.field === currentField);
      if (!isColumnDefined) {
        return;
      }
    }

    setSortModel(newModel);
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  };

  // ── Action handlers ───────────────────────────────────────────────────
  const openAction = (row, actionStatus) => {
    setActionComment('');
    setPendingAction({ row, status: actionStatus });
  };

  const closeAction = () => { setActionComment(''); setPendingAction(null); };

  const handleConfirmAction = async () => {
    if (!pendingAction) return;
    const { row, status: actionStatus } = pendingAction;
    setPendingAction(null);
    setActionComment('');
    try {
      setActionLoading((prev) => ({ ...prev, [row.id]: actionStatus }));
      await performanceService.updateReviewStatus({
        id: row.id,
        reviewedById: userId,
        reviewedByName: reviewerName,
        status: actionStatus,
        comments: actionComment || null,
      });
      showSnackbar(
        `Review marked as ${actionStatus === REVIEW_STATUS.COMPLETED ? 'completed' : 'skipped'}.`,
        'success'
      );
      fetchReviews();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || 'Failed to update review.';
      showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
    } finally {
      setActionLoading((prev) => { const n = { ...prev }; delete n[row.id]; return n; });
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      field: 'userName',
      headerName: 'Employee',
      flex: 1,
      renderCell: (p) => (
        <Tooltip title={p.value ?? ''}><span className="leave-ellipsis">{p.value ?? '—'}</span></Tooltip>
      ),
    },
    ...(showMilestone ? [{
      field: 'milestoneValue',
      headerName: 'Milestone',
      flex: 0.7,
      sortable: false,
      renderCell: (p) => p.value != null ? `Month ${p.value}` : '—',
    }] : []),
    {
      field: 'triggerDate',
      headerName: 'Trigger Date',
      flex: 0.9,
      renderCell: (p) => fmtDate(p.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      renderCell: (p) => {
        const { color, label } = statusChipProps(p.value);
        return (
          <Chip
            label={label}
            color={color}
            size="small"
            sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}
          />
        );
      },
    },
    {
      field: 'reviewedByName',
      headerName: 'Reviewed By',
      flex: 1,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title={p.value ?? ''}><span className="leave-ellipsis">{p.value || '—'}</span></Tooltip>
      ),
    },
    {
      field: 'reviewedAt',
      headerName: 'Reviewed At',
      flex: 0.9,
      renderCell: (p) => fmtDate(p.value),
    },
    ...(reviewType !== REVIEW_TYPE.QUARTERLY_PERFORMANCE_REVIEW ? [{
      field: 'comments',
      headerName: 'Comments',
      flex: 1,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title={p.value ?? ''}><span className="leave-ellipsis">{p.value || '—'}</span></Tooltip>
      ),
    }] : []),
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.6,
      sortable: false,
      renderCell: (p) => {
        if (p.row.status === REVIEW_STATUS.COMPLETED) {
          if (p.row.reviewType === REVIEW_TYPE.QUARTERLY_PERFORMANCE_REVIEW) {
            return (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => navigate(`/home/quarterly-review/${p.row.id}`, { state: { review: p.row } })}
                className="leave-action-btn"
                startIcon={<AssessmentRoundedIcon sx={{ fontSize: '15px !important' }} />}
                sx={{ minWidth: 88, textTransform: 'none' }}
              >
                View Form
              </Button>
            );
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                Completed
              </Typography>
            </Box>
          );
        }
        if (p.row.status === REVIEW_STATUS.SKIPPED) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <RemoveCircleOutlineIcon sx={{ fontSize: 16, color: '#9e9e9e' }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#9e9e9e' }}>
                Skipped
              </Typography>
            </Box>
          );
        }

        const isQuarterly = p.row.reviewType === REVIEW_TYPE.QUARTERLY_PERFORMANCE_REVIEW;

        // For Quarterly reviews, manager acts when EMPLOYEE_SUBMITTED
        if (isQuarterly) {
          if (p.row.status === REVIEW_STATUS.PENDING) {
            return (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Waiting on Employee
              </Typography>
            );
          }
          if (p.row.status === REVIEW_STATUS.EMPLOYEE_SUBMITTED) {
            return (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => navigate(`/home/quarterly-review/${p.row.id}`, { state: { review: p.row } })}
                className="leave-action-btn"
                startIcon={<EditNoteRoundedIcon sx={{ fontSize: '15px !important' }} />}
                sx={{ minWidth: 88, textTransform: 'none' }}
              >
                Review Form
              </Button>
            );
          }
        }

        // For other reviews, manager acts when PENDING
        if (p.row.status !== REVIEW_STATUS.PENDING) return null;

        const id = p.row.id;
        const busy = Boolean(actionLoading[id]);
        const isComp = actionLoading[id] === REVIEW_STATUS.COMPLETED;
        const isSkip = actionLoading[id] === REVIEW_STATUS.SKIPPED;
        return (
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', py: 0.5 }}>
            <Button
              id={`complete-review-${id}`}
              size="small"
              variant="contained"
              color="success"
              disabled={busy}
              onClick={() => openAction(p.row, REVIEW_STATUS.COMPLETED)}
              className="leave-action-btn"
              startIcon={isComp ? undefined : <CheckCircleOutlineIcon sx={{ fontSize: '15px !important' }} />}
              sx={{ minWidth: 88 }}
            >
              {isComp ? '…' : 'Complete'}
            </Button>
            <Button
              id={`skip-review-${id}`}
              size="small"
              variant="outlined"
              color="warning"
              disabled={busy}
              onClick={() => openAction(p.row, REVIEW_STATUS.SKIPPED)}
              className="leave-action-btn"
              startIcon={isSkip ? undefined : <RemoveCircleOutlineIcon sx={{ fontSize: '15px !important' }} />}
              sx={{ minWidth: 72 }}
            >
              {isSkip ? '…' : 'Skip'}
            </Button>
          </Box>
        );
      },
    },
  ], [showMilestone, actionLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Empty state ───────────────────────────────────────────────────────
  const renderEmptyState = () => (
    <div className="leave-empty-state">
      <div className="leave-empty-icon-wrap">
        <AssessmentRoundedIcon className="leave-empty-icon" />
      </div>
      <Typography variant="h6" className="leave-empty-title">No Reviews Found</Typography>
      <Typography className="leave-empty-subtitle">
        {status || debouncedSearch
          ? 'No reviews match your current filters.'
          : `No ${REVIEW_TYPE_LABELS[reviewType]} reviews found.`}
      </Typography>
    </div>
  );

  // ── Access guard ──────────────────────────────────────────────────────
  if (!canAccess) {
    return (
      <div className="leave-page-container">
        <PageHeader title="Employee Monitoring" />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <Typography color="text.secondary">You do not have permission to view this page.</Typography>
        </Box>
      </div>
    );
  }

  return (
    <div className="leave-page-container">
      <PageHeader
        title="Employee Monitoring"
        right={
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />

      {SnackbarComponent}

      {/* ── Action dialog ────────────────────────────────────────────── */}
      <ReviewActionDialog
        open={Boolean(pendingAction)}
        status={pendingAction?.status}
        row={pendingAction?.row}
        comment={actionComment}
        onCommentChange={setActionComment}
        onConfirm={handleConfirmAction}
        onClose={closeAction}
        fmtDate={fmtDate}
      />

      <Paper elevation={0} className="leave-card">
        {/* ── Header strip ─────────────────────────────────────────── */}
        <div className="leave-card-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <MonitorHeartRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />
            <Typography className="leave-card-header-title" variant="subtitle1">
              Performance Reviews
            </Typography>
          </Box>

          {/* Status filter + search */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPaginationModel((p) => ({ ...p, page: 0 })); }}
              size="small"
              displayEmpty
              renderValue={(val) => val === '' ? 'All Status' : REVIEW_STATUS_OPTIONS.find(o => o.value === val)?.label ?? val}
              sx={{ minWidth: 130, height: 34, bgcolor: '#fff', borderRadius: 1 }}
            >
              {REVIEW_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="leave-search-input"
            />
          </Box>
        </div>

        {/* ── Review type tabs ─────────────────────────────────────── */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            px: 1.5,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' },
            '& .Mui-selected': { color: 'var(--primary) !important' },
            '& .MuiTabs-indicator': { backgroundColor: 'var(--primary)' },
          }}
        >
          {visibleTabs.map((tab, i) => (
            <Tab key={tab.value} label={tab.label} id={`review-tab-${i}`} />
          ))}
        </Tabs>

        {/* ── Content ──────────────────────────────────────────────── */}
        {loading && records.length === 0 ? (
          <Box sx={{ p: 2 }}><TableSkeleton rows={5} columns={6} /></Box>
        ) : !loading && rowCount === 0 ? (
          renderEmptyState()
        ) : (
          <div className="leave-data-table">
            <div className="leave-data-grid">
              <DataGrid
                rows={records}
                columns={columns}
                getRowId={(row) => row.id}
                pagination
                paginationMode="server"
                sortingMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50]}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                loading={loading}
                disableRowSelectionOnClick
                sx={{
                  height: '100%',
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': { bgcolor: 'rgba(4,88,100,0.04)' },
                  '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                  '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(4,88,100,0.03)' },
                }}
              />
            </div>
          </div>
        )}
      </Paper>
    </div>
  );
};

export default EmployeeMonitoring;
